import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Input, Select } from 'antd';
import { FilterBar } from '../../../components/list';
import type { ContactType, ContactRole } from '../types';
import { normalizeContactType } from '../contactType';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useDebounce } from '../../../hooks/useDebounce';
import { LAST_ACTIVITY_OPTIONS } from '../lastActivityLabels';

const { Search } = Input;
const { RangePicker } = DatePicker;

export interface ContactFilterValues {
  search?: string;
  phone?: string;
  categoryId?: number;
  contactType?: ContactType;
  role?: ContactRole;
  ownerUserId?: string;
  createdRange?: [Dayjs | null, Dayjs | null] | null;
  segmentIds?: string[];
  contactCategoryIds?: number[];
  lastActivity?: string;
  createdFrom?: string;
  createdTo?: string;
}

export interface ContactFilterBarProps {
  value: ContactFilterValues;
  onChange: (value: ContactFilterValues) => void;
  onReset: () => void;
  categoryOptions: { value: number; label: string }[];
  roleOptions: { value: number; label: string }[];
  ownerOptions: { value: string; label: string }[];
  loading?: boolean;
  segmentOptions?: { value: string; label: string }[];
  contactCategoryOptions?: { value: number; label: string }[];
}

const contactTypeOptions: { value: ContactType; label: string }[] = [
  { value: 0, label: 'Individual' },
  { value: 1, label: 'Organization' },
];

const ContactFilterBar: React.FC<ContactFilterBarProps> = ({
  value,
  onChange,
  onReset,
  roleOptions,
  ownerOptions,
  loading = false,
  segmentOptions = [],
  contactCategoryOptions = [],
}) => {
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useEffect(() => { valueRef.current = value; }, [value]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const lastCommittedRef = useRef(value.search ?? '');
  const lastCommittedPhoneRef = useRef(value.phone ?? '');
  const isEditingRef = useRef(false);

  const [localSearch, setLocalSearch] = useState(value.search ?? '');
  const debouncedSearch = useDebounce(localSearch, 300);

  const [localPhone, setLocalPhone] = useState(value.phone ?? '');
  const debouncedPhone = useDebounce(localPhone, 300);

  // Sync debounced value to parent (passive debounce — fires ~300ms after typing stops)
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  }, [debouncedSearch]);

  // Sync debounced phone to parent
  useEffect(() => {
    const trimmed = debouncedPhone.trim();
    if (trimmed === lastCommittedPhoneRef.current) return;
    lastCommittedPhoneRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, phone: trimmed || undefined });
  }, [debouncedPhone]);

  // Sync from parent (e.g., external reset) — but never clobber the input while
  // the user is actively typing, since the URL value lags and can arrive as a
  // stale/undefined search, wiping in-progress keystrokes.
  useEffect(() => {
    if (isEditingRef.current) return;
    setLocalSearch(value.search ?? '');
    setLocalPhone(value.phone ?? '');
  }, [value.search, value.phone]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    isEditingRef.current = true;
    setLocalSearch(val);
    if (val === '') {
      // Clear (X) button — fire immediately
      if (lastCommittedRef.current === '') return;
      lastCommittedRef.current = '';
      onChangeRef.current({ ...valueRef.current, search: undefined });
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    isEditingRef.current = true;
    setLocalPhone(val);
    if (val === '') {
      if (lastCommittedPhoneRef.current === '') return;
      lastCommittedPhoneRef.current = '';
      onChangeRef.current({ ...valueRef.current, phone: undefined });
    }
  };

  const handleSearch = (v: string) => {
    // Enter key or search button — fire immediately (no debounce)
    isEditingRef.current = true;
    const trimmed = v.trim();
    setLocalSearch(v);
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  };

  const handlePhoneSearch = (v: string) => {
    isEditingRef.current = true;
    const trimmed = v.trim();
    setLocalPhone(v);
    if (trimmed === lastCommittedPhoneRef.current) return;
    lastCommittedPhoneRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, phone: trimmed || undefined });
  };

  const handleTypeChange = (v: ContactType | undefined) => {
    onChange({
      ...value,
      contactType: v === undefined ? undefined : normalizeContactType(v),
    });
  };
  const handleOwnerChange = (v: string | undefined) => {
    onChange({ ...value, ownerUserId: v });
  };
  const handleRangeChange = (range: [Dayjs | null, Dayjs | null] | null) => {
    onChange({ ...value, createdRange: range });
  };

  const rangeValue = useMemo(() => {
    if (value.createdRange) return value.createdRange;
    if (value.createdFrom && value.createdTo) {
      return [dayjs(value.createdFrom), dayjs(value.createdTo)] as [Dayjs, Dayjs];
    }
    return undefined;
  }, [value.createdRange, value.createdFrom, value.createdTo]);

  const hasActiveFilters =
    Boolean(value.search) ||
    Boolean(value.phone) ||
    value.contactType !== undefined ||
    value.role !== undefined ||
    value.ownerUserId !== undefined ||
    (value.createdRange !== undefined && value.createdRange !== null) ||
    (value.createdFrom !== undefined && value.createdFrom !== '') ||
    (value.createdTo !== undefined && value.createdTo !== '') ||
    (value.segmentIds !== undefined && value.segmentIds.length > 0) ||
    (value.contactCategoryIds !== undefined && value.contactCategoryIds.length > 0) ||
    Boolean(value.lastActivity);

  const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' };
  const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };

  return (
    <FilterBar onReset={hasActiveFilters ? onReset : undefined}>
      <div style={box}>
        <span style={lbl}>Search</span>
        <Search
          allowClear
          onSearch={handleSearch}
          onChange={handleSearchChange}
          onBlur={() => { isEditingRef.current = false; }}
          value={localSearch}
          placeholder="Search name or company..."
          style={{ width: 240 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Phone</span>
        <Search
          allowClear
          onSearch={handlePhoneSearch}
          onChange={handlePhoneChange}
          onBlur={() => { isEditingRef.current = false; }}
          value={localPhone}
          placeholder="Search phone only..."
          style={{ width: 200 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Type</span>
        <Select<ContactType>
          allowClear
          options={contactTypeOptions}
          value={value.contactType}
          onChange={handleTypeChange}
          style={{ width: 140 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Role</span>
        <Select<ContactRole>
          allowClear
          options={roleOptions}
          value={value.role}
          onChange={(v) => onChange({ ...value, role: v })}
          style={{ width: 120 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Owner</span>
        <Select
          allowClear
          options={ownerOptions}
          value={value.ownerUserId ?? undefined}
          onChange={handleOwnerChange}
          loading={loading}
          style={{ width: 180 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Created Date</span>
        <RangePicker
          value={rangeValue}
          onChange={handleRangeChange}
        />
      </div>
      {segmentOptions.length > 0 ? (
        <div style={box}>
          <span style={lbl}>Customer Segments</span>
          <Select<string[]>
            mode="multiple"
            allowClear
            options={segmentOptions}
            value={value.segmentIds}
            onChange={(v) => onChange({ ...value, segmentIds: v.length > 0 ? v : undefined })}
            style={{ minWidth: 200 }}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </div>
      ) : null}
      {contactCategoryOptions.length > 0 ? (
        <div style={box}>
          <span style={lbl}>Contact Categories</span>
          <Select<number[]>
            mode="multiple"
            allowClear
            options={contactCategoryOptions}
            value={value.contactCategoryIds}
            onChange={(v) => onChange({ ...value, contactCategoryIds: v.length > 0 ? v : undefined })}
            style={{ minWidth: 200 }}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </div>
      ) : null}
      <div style={box}>
        <span style={lbl}>Last Activity</span>
        <Select<string>
          allowClear
          options={LAST_ACTIVITY_OPTIONS}
          value={value.lastActivity}
          onChange={(v) => onChange({ ...value, lastActivity: v || undefined })}
          placeholder="Any"
          style={{ width: 180 }}
          optionFilterProp="label"
        />
      </div>
    </FilterBar>
  );
};

export default ContactFilterBar;
